import { useState } from "react";
import { Trash2, Plus, Loader2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Decorative illustration
// ---------------------------------------------------------------------------

function TrashIllustration() {
  return (
    <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
      <div className="relative">
        <Plus className="absolute -top-8 -right-8 h-6 w-6 text-(--color-danger-text)/70" strokeWidth={2.5} aria-hidden />
        <Plus className="absolute top-6 -left-10 h-5 w-5 text-(--color-danger-text)/70" strokeWidth={2.5} aria-hidden />
        <Plus className="absolute -top-12 left-1 h-4 w-4 text-(--color-danger-text)/40" strokeWidth={2.5} aria-hidden />
        <span className="absolute -top-10 right-4 h-1.5 w-1.5 rounded-full bg-(--color-danger)/60" aria-hidden />
        <span className="absolute top-8 -right-10 h-1.5 w-1.5 rounded-full bg-(--color-danger)/40" aria-hidden />
        <span className="absolute top-14 -left-6 h-1.5 w-1.5 rounded-full bg-(--color-danger)/60" aria-hidden />
        <div
          className="absolute -bottom-2 left-1/2 -translate-x-1/2 h-3 w-28 rounded-full bg-(--color-danger)/40 blur-md"
          aria-hidden
        />
        <Trash2 className="h-28 w-28 text-(--color-danger-text)" strokeWidth={1.5} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  onConfirm: () => Promise<void>;
}

export function DeleteConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const { t } = useLingui();
  const [isDeleting, setIsDeleting] = useState(false);

  async function handleConfirm() {
    setIsDeleting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // The API client already shows an error toast for the failure.
      // Close so the toast is not aria-hidden behind the dialog overlay.
      onOpenChange(false);
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isDeleting) onOpenChange(v); }}>
      <DialogContent className="max-w-[31.25rem] p-0 gap-0 overflow-hidden" hideClose>
        {/* Illustration */}
        <div className="h-[15.5rem] flex items-center justify-center p-5">
          <TrashIllustration />
        </div>

        {/* Text */}
        <div className="flex flex-col items-center gap-3 px-5 pb-5 text-center">
          <DialogTitle className="text-2xl font-medium text-(--color-text-primary)">
            {title}
          </DialogTitle>
          <DialogDescription className="text-base text-(--color-text-secondary) leading-snug">
            {description}
          </DialogDescription>
        </div>

        {/* Actions */}
        <div className="flex gap-3 px-5 pb-5">
          <DialogClose asChild>
            <Button
              variant="outline"
              disabled={isDeleting}
              className="flex-1 h-13 rounded-[0.625rem] text-base font-medium"
            >
              {t`Cancel`}
            </Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={isDeleting}
            onClick={handleConfirm}
            className="flex-1 h-13 rounded-[0.625rem] text-base font-medium gap-2"
          >
            {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isDeleting ? t`Deleting...` : t`Delete`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
